import type { FUSportsLivescoreCompetitionGroup, SearchEntityResult } from "fansunited-data-layer";
import type { LivescoreFilter } from "./livescore";

/**
 * Order the livescore endpoint's already-grouped-by-competition array
 * according to the active filter. Competition IDs coming from the endpoint
 * are native FansUnited IDs (e.g. `fb:c:1`), matching the popular/favourite
 * ID lists 1:1.
 *
 * - "popular":    keep only groups whose competitionId is in popularIds, config order.
 * - "my-leagues": keep only groups whose competitionId is in favouriteIds, user order.
 * - "all":        favourites first (user order), then popular (config order), then the rest.
 * - other filters (live, upcoming, finished): popular first, then the rest.
 */
export function applyGroupOrdering(
    groups: FUSportsLivescoreCompetitionGroup[],
    filter: LivescoreFilter,
    popularIds: string[],
    favouriteIds: string[]
): FUSportsLivescoreCompetitionGroup[] {
    if (filter === "my-leagues") {
        const favMap = new Map(favouriteIds.map((id, i) => [id, i]));
        return groups
            .filter((g) => favMap.has(g.competitionId))
            .sort((a, b) => favMap.get(a.competitionId)! - favMap.get(b.competitionId)!);
    }

    const popularIndexMap = new Map(popularIds.map((id, index) => [id, index]));

    if (filter === "popular") {
        return groups
            .filter((g) => popularIndexMap.has(g.competitionId))
            .sort((a, b) => popularIndexMap.get(a.competitionId)! - popularIndexMap.get(b.competitionId)!);
    }

    const favIndexMap = new Map(favouriteIds.map((id, i) => [id, i]));
    const includeFavSection = filter === "all";

    const favGroups = includeFavSection
        ? groups
              .filter((g) => favIndexMap.has(g.competitionId))
              .sort((a, b) => favIndexMap.get(a.competitionId)! - favIndexMap.get(b.competitionId)!)
        : [];

    const favIdSet = new Set(favGroups.map((g) => g.competitionId));

    const popularGroups = groups
        .filter((g) => popularIndexMap.has(g.competitionId) && !favIdSet.has(g.competitionId))
        .sort((a, b) => popularIndexMap.get(a.competitionId)! - popularIndexMap.get(b.competitionId)!);

    const otherGroups = groups.filter((g) => !popularIndexMap.has(g.competitionId) && !favIdSet.has(g.competitionId));

    return [...favGroups, ...popularGroups, ...otherGroups];
}

export interface PartitionedGroups {
    /** Rendered open, teams hydrated: favourites, then popular (config order), then — for my-leagues — nothing else. */
    expanded: FUSportsLivescoreCompetitionGroup[];
    /** The rest of the day in the API's order, rendered as headers until the user opens one. */
    collapsed: FUSportsLivescoreCompetitionGroup[];
}

/**
 * Split the two livescore stages into what renders open and what renders collapsed.
 *
 * `popularGroups` come from the fully hydrated popular board (stage 1); `restGroups` from the
 * whole day hydrated to competition level (stage 2). A competition present in both is taken from
 * the popular board, which carries its team entities. Favourites outside the popular list come
 * from the rest and are expanded too — the client hydrates their teams on arrival.
 *
 * - "popular":    the popular block only, config order; nothing collapsed.
 * - "my-leagues": favourite groups only, user order; nothing collapsed.
 * - everything else ("all", live, upcoming, finished): favourites (user order), then popular
 *   (config order), then the remaining rest groups collapsed in API order.
 */
export function partitionGroups(
    popularGroups: FUSportsLivescoreCompetitionGroup[],
    restGroups: FUSportsLivescoreCompetitionGroup[],
    filter: LivescoreFilter,
    popularIds: string[],
    favouriteIds: string[]
): PartitionedGroups {
    const byId = new Map<string, FUSportsLivescoreCompetitionGroup>();
    for (const group of restGroups) byId.set(group.competitionId, group);
    for (const group of popularGroups) byId.set(group.competitionId, group);

    if (filter === "popular") {
        return { expanded: applyGroupOrdering(popularGroups, "popular", popularIds, []), collapsed: [] };
    }
    if (filter === "my-leagues") {
        return {
            expanded: applyGroupOrdering([...byId.values()], "my-leagues", popularIds, favouriteIds),
            collapsed: [],
        };
    }

    const favourites = new Set(favouriteIds);
    const popular = new Set(popularIds);
    const open = [...byId.values()].filter((g) => favourites.has(g.competitionId) || popular.has(g.competitionId));
    const expanded = applyGroupOrdering(open, "all", popularIds, favouriteIds);
    const openIds = new Set(expanded.map((g) => g.competitionId));
    const collapsed = restGroups.filter((g) => !openIds.has(g.competitionId));
    return { expanded, collapsed };
}

/** True when every competitor on the group has an entity — i.e. its rows can show names. */
export function isGroupHydrated(
    group: FUSportsLivescoreCompetitionGroup,
    entities: Record<string, SearchEntityResult>
): boolean {
    for (const event of group.events) {
        const one = event.competitorOne?.id;
        const two = event.competitorTwo?.id;
        if ((one && !entities[one]) || (two && !entities[two])) return false;
    }
    return true;
}
